import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  PageHeader,
  SectionHeader,
  StatCard,
  StatGrid,
  usePageTitle,
} from '@xinfer/ui';
import { Activity, CircleDollarSign, Database, Percent } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TokenComposition } from '../components/TokenComposition';
import { CreditWindowBar } from '../components/CreditWindowBar';
import {
  cacheHitRate,
  explainCredits,
  explainKvTokenSeconds,
  formatCredits,
  formatInteger,
  formatPercent,
  windowFraction,
} from '../lib/format';

/**
 * What the user wants to know on landing: what have I spent, and how much room
 * do I have left.
 */
export function Overview({ account }: { account: Account }) {
  usePageTitle('Overview');

  const usage = useQuery({
    queryKey: ['usage', account.id],
    queryFn: () => client.usage(account.id),
  });

  const credits = useQuery({
    queryKey: ['credits', account.id],
    queryFn: () => client.credits(account.id),
  });

  const hitRate = usage.data
    ? cacheHitRate(usage.data.input_tokens, usage.data.cached_input_tokens)
    : null;

  // The window that will stop a request first: the narrowest exhausted one,
  // or failing that the fullest. It is the only one worth putting at the top
  // of a landing page — a monthly total with plenty left says nothing about
  // whether the next request goes through.
  const binding = (() => {
    const windows = credits.data?.windows.filter((w) => w.cap_milli !== null) ?? [];
    if (windows.length === 0) {
      return null;
    }
    return (
      windows.find((w) => (windowFraction(w.used_milli, w.cap_milli) ?? 0) >= 1) ??
      windows.reduce((worst, w) =>
        (windowFraction(w.used_milli, w.cap_milli) ?? 0) >
        (windowFraction(worst.used_milli, worst.cap_milli) ?? 0)
          ? w
          : worst,
      )
    );
  })();

  return (
    <>
      <PageHeader
        title="Overview"
        description="What this account has spent, and how much room is left before a request is refused."
      />

      {usage.isError && (
        <ErrorState
          title="Could not load usage"
          description="The figures below are read from the ledger, which is behind the api rather than on the serving path — inference is unaffected."
        />
      )}

      {!usage.isError && (
        <>
          <StatGrid>
            <StatCard
              label="Requests"
              value={usage.data ? formatInteger(usage.data.requests) : '—'}
              hint="completed inference calls"
              icon={<Activity />}
              loading={usage.isLoading}
            />
            <StatCard
              label="Credits used"
              value={usage.data ? formatCredits(usage.data.credits_milli) : '—'}
              hint={usage.data ? explainCredits(usage.data.credits_milli) : undefined}
              icon={<CircleDollarSign />}
              loading={usage.isLoading}
              explain="One credit is 1,000 weighted tokens: an uncached prompt token counts 1, a cached one 1/50th, an output token 2. This is the whole rate card — there is no request count and no per-model multiplier."
            />
            <StatCard
              label="KV-token-seconds"
              value={usage.data ? formatInteger(usage.data.kv_token_seconds) : '—'}
              // Still metered, no longer billed. Kept on the page because it
              // is the honest measure of what a request costs the fleet, and
              // it is the evidence that would justify re-weighting output.
              hint={usage.data ? explainKvTokenSeconds(usage.data.kv_token_seconds) : undefined}
              icon={<Database />}
              loading={usage.isLoading}
              explain="KV memory held, multiplied by how long it was held for — the honest measure of what a request costs this hardware. Metered but not charged for: the published rate card prices tokens, and billing the same work twice would be dishonest."
            />
            <StatCard
              label="Cache hit rate"
              value={hitRate === null ? '—' : formatPercent(hitRate)}
              hint={
                usage.data
                  ? `${formatInteger(usage.data.cached_input_tokens)} of ${formatInteger(
                      usage.data.input_tokens,
                    )} prompt tokens`
                  : undefined
              }
              icon={<Percent />}
              loading={usage.isLoading}
              // Derived, not fetched, and the one figure on this page a user
              // can move: a cache hit skips prefill and is billed at its own
              // lower rate, so putting the stable part of a prompt first is
              // worth real money.
              explain="The share of prompt tokens served from the prefix cache. Cache hits skip prefill and are billed at a lower rate, so putting the unchanging part of a prompt first raises this."
            />
          </StatGrid>

          {binding && (
            <div className="mt-6">
              <SectionHeader
                title="Closest limit"
                description="The window that will refuse a request first. The other two are on the plan page."
              />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <CreditWindowBar usage={binding} />
                <div className="text-sm leading-relaxed text-muted-foreground sm:col-span-1 lg:col-span-2">
                  <p>
                    Credits are capped over three rolling windows — 5 hours, 7 days and 30 days —
                    and the narrow one is the one that bites during a busy session. It rolls
                    continuously rather than resetting at a fixed hour, so headroom comes back
                    gradually rather than all at once.
                  </p>
                  <p className="mt-3">
                    <Link className="font-medium text-foreground underline" to="/credits">
                      See all three windows and what the plan includes
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Token composition</CardTitle>
                <CardDescription>
                  Prompt tokens split by cache outcome, against output. Cached prompt tokens are a
                  subset of the prompt total, never an addition to it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {usage.data ? (
                  <TokenComposition usage={usage.data} />
                ) : (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Where the credits go</CardTitle>
                <CardDescription>
                  The cache hit rate above is the one number on this page you can move.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                <p>
                  A prompt token served from the prefix cache costs a fiftieth of one that has to
                  be computed, because a cache hit skips prefill entirely. An agent that re-sends
                  the same large context every turn therefore pays for it once, not once per turn.
                </p>
                <p>
                  The practical consequence: put the parts of a prompt that do not change — system
                  instructions, tool schemas, retrieved documents — at the front, and the parts
                  that do at the end. Anything before the first change is reusable; everything
                  after it has to be recomputed.
                </p>
                <p>
                  Output is weighted at twice an uncached prompt token, so capping{' '}
                  <code className="font-mono">max_tokens</code> to what a turn actually needs is
                  the other lever worth pulling.
                </p>
              </CardContent>
            </Card>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Gaps between what the gate meters and what appears here are expected and bounded:
            usage ships on a cursor that only advances on acknowledgement, so figures lag rather
            than go missing.
          </p>
        </>
      )}
    </>
  );
}
